import { useState, useMemo } from 'react'
import { ArrowLeft, X, Trash2 } from 'lucide-react'
import { useMatch, useAddMatchPlayer, useRemoveMatchPlayer } from '../hooks/useMatches'
import { useDeletePlayer } from '../hooks/usePlayers'
import { useAuth } from '../contexts/AuthContext'
import { getInitials } from '../utils/colors'
import { hasHandicapIndex, formatHandicapIndex } from '../utils/handicaps'
import { getMatchTypeLabels } from '../utils/matchTypeLabels'
import { usePlayerSearch } from '../hooks/usePlayerSearch'
import ManualPlayerForm from './shared/ManualPlayerForm'
import PlayerSearchInput from './shared/PlayerSearchInput'
import PlayerSearchResults from './shared/PlayerSearchResults'

export default function AddPlayersView({ matchId, onBack }) {
  const { user } = useAuth()
  const { data: match } = useMatch(matchId)
  const addMatchPlayer = useAddMatchPlayer()
  const removeMatchPlayer = useRemoveMatchPlayer()
  const deletePlayer = useDeletePlayer()

  const [searchText, setSearchText] = useState('')
  const [showManualForm, setShowManualForm] = useState(false)

  const matchPlayers = match?.match_players || []
  const isOwner = match?.owner_id === user?.id
  const labels = getMatchTypeLabels(match?.match_type)
  const matchType = match?.match_type || 'quick_round'
  const maxPlayers = matchType === 'quick_round' ? 4 : matchType === 'team_match' || matchType === 'invitational' ? Infinity : 8
  const atPlayerLimit = matchPlayers.length >= maxPlayers

  // Players already in the match (by player_id)
  const matchPlayerIds = useMemo(() => new Set(matchPlayers.map((mp) => mp.player_id)), [matchPlayers])

  const {
    matchingFollowedPlayers,
    localSearchResults,
    matchingGroups,
    followedPlayerIds,
    allPlayers,
    myFollows,
    isSearching,
  } = usePlayerSearch({ excludeIds: matchPlayerIds, searchText })

  const handleAddExistingPlayer = (playerId) => {
    addMatchPlayer.mutate({ match_id: matchId, player_id: playerId })
  }

  const handleRemovePlayer = (matchPlayerId) => {
    removeMatchPlayer.mutate({ id: matchPlayerId, match_id: matchId })
  }

  const handleAddGroup = (group) => {
    const members = group.members || []
    const toAdd = members
      .filter((m) => m.player_id && !matchPlayerIds.has(m.player_id))
      .map((m) => m.player_id)
    if (toAdd.length === 0) return
    const limit = maxPlayers - matchPlayers.length
    const adding = toAdd.slice(0, limit)
    adding.forEach((playerId) => {
      addMatchPlayer.mutate({ match_id: matchId, player_id: playerId })
    })
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={onBack}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-tertiary)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Add Players</h1>
      </div>

      {/* Current match players */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        padding: '1rem',
        marginBottom: '1rem',
      }}>
        <h3 style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '0.75rem',
        }}>
          {labels.inThisLabel} ({matchPlayers.length})
        </h3>

        {matchPlayers.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '0.5rem 0' }}>
            No players yet
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {matchPlayers.map((mp) => {
              const player = mp.player
              if (!player) return null
              const isMatchOwner = mp.user_id != null && mp.user_id === match?.owner_id

              return (
                <div
                  key={mp.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    padding: '0.5rem 0.25rem',
                    borderBottom: '1px solid var(--border-color)',
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: player.avatar_color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '0.75rem',
                    color: '#fff',
                    flexShrink: 0,
                  }}>
                    {getInitials(player)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                      {player.first_name || player.last_name
                        ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
                        : player.display_name}
                    </span>
                    {isMatchOwner && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.625rem',
                        fontWeight: '600',
                        color: 'var(--accent-primary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        Owner
                      </span>
                    )}
                    {!player.user_id && !isMatchOwner && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.5625rem',
                        fontWeight: '600',
                        color: 'var(--text-muted)',
                        padding: '0.0625rem 0.375rem',
                        borderRadius: '0.25rem',
                        backgroundColor: 'var(--bg-primary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        Guest
                      </span>
                    )}
                  </div>
                  {hasHandicapIndex(mp.handicap_index) && (
                    <span style={{
                      width: '52px',
                      textAlign: 'center',
                      padding: '0.125rem 0.25rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                      flexShrink: 0,
                    }}>
                      {formatHandicapIndex(mp.handicap_index)}
                    </span>
                  )}
                  {isOwner && !isMatchOwner && (
                    <button
                      onClick={() => handleRemovePlayer(mp.id)}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '9999px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--accent-danger, #ef4444)',
                        flexShrink: 0,
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {matchPlayers.length > 0 && (
          <p style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            textAlign: 'center',
            marginTop: '0.75rem',
            fontStyle: 'italic',
          }}>
            Handicaps can be added on the next page
          </p>
        )}
      </div>

      {/* Done button */}
      <button
        onClick={onBack}
        className="btn-primary"
        style={{
          width: '100%',
          height: '48px',
          fontSize: '1rem',
          fontWeight: '600',
          marginTop: '0.75rem',
          marginBottom: '1rem',
          borderRadius: '9999px',
        }}
      >
        Done
      </button>

      {/* Quick round player limit message */}
      {atPlayerLimit && (
        <div style={{
          padding: '0.625rem 1rem',
          marginBottom: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.25)',
          fontSize: '0.8125rem',
          color: 'var(--text-secondary)',
          textAlign: 'center',
        }}>
          {labels.singular} is limited to {maxPlayers} players
        </div>
      )}

      {/* Add Players heading + Manual add form */}
      {!atPlayerLimit && (
        <h2 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-primary)', marginBottom: '0.75rem' }}>
          Add Players
        </h2>
      )}
      {!atPlayerLimit && (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <ManualPlayerForm
              onPlayerCreated={(newPlayer) => {
                addMatchPlayer.mutate({ match_id: matchId, player_id: newPlayer.id })
              }}
              excludeIds={matchPlayerIds}
              allPlayers={allPlayers}
              collapsed={!showManualForm}
              onToggle={() => setShowManualForm((v) => !v)}
              onDedupAdd={handleAddExistingPlayer}
            />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem',
          }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '500' }}>or</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <PlayerSearchInput value={searchText} onChange={setSearchText} />
          </div>
        </>
      )}

      {!atPlayerLimit && (
        <PlayerSearchResults
          matchingFollowedPlayers={matchingFollowedPlayers}
          localSearchResults={localSearchResults}
          matchingGroups={matchingGroups}
          followedPlayerIds={followedPlayerIds}
          myFollows={myFollows}
          onAddPlayer={handleAddExistingPlayer}
          onAddGroup={handleAddGroup}
          isSearching={isSearching}
          addPending={addMatchPlayer.isPending}
          excludeIds={matchPlayerIds}
        />
      )}
    </div>
  )
}
